import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAppNavigationType } from "../utils/appNavigationType.js";
import { hasMoreBelow } from "../utils/scrollCue.js";
import { formatDisplayPrice } from "../utils/currency.js";
import { CATEGORY_BY_KEY, CATEGORY_GROUPS, categoryOf } from "../utils/itemCategories.js";
import Footer from "../components/Footer.jsx";
import ProductCard from "../components/ProductCard.jsx";
import SlotFilter from "../components/SlotFilter.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { useFeaturedSearch } from "../context/FeaturedSearchContext.jsx";
import { useHeader } from "../context/HeaderContext.jsx";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { useTitleRowPx } from "../hooks/useTitleRowPx.js";
import { isMobileViewport, isTouchDevice } from "../utils/viewport.js";
import { primeSoftKeyboard } from "../utils/softKeyboard.js";
import { dummyFetch } from "../utils/dummyListings.js";
import { consumeCatalogSearchRequest, onCatalogSearchRequest } from "../utils/catalogSearchSignal.js";

// The FILTER panel's orders (the owner's spec, 2026-09-30; see
// PROMPT_COMPILER_CATALOG_FILTER_PANEL.md). NEWEST first and by default:
// every piece is one of one and sells once, so Most Popular only ranked what
// was already gone. Sold is a filter now (AVAILABILITY_OPTIONS), and A-Z
// went, since every title starts with its brand; the BRAND filter does
// that job.
const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
];

// Availability, AVAILABLE by default. SOLD is the SOLD page itself and ALL
// rides on ?status=all, so the title always names what is on screen (see
// pickAvailability).
// ALL first (the owner, 2026-10-17: "put all first"); AVAILABLE stays the
// default.
const AVAILABILITY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "available", label: "Available" },
  { value: "sold", label: "Sold" },
];

const isSoldOutItem = (item) => Boolean(item.sold || item.state !== "LIVE" || !item.is_available);
const brandOf = (item) => (item.brand_name || item.brand || "").trim();

// A piece's size as a SIZE chip: the size alone, without the colour the
// inventory keeps beside it or the fit note in brackets ("44 (fits like M),
// Black" -> "44"). The cards show the fuller label; the chip only has to say
// which size.
function sizeChipOf(raw) {
  if (!raw) return null;
  const size = String(raw).split(",")[0].split("(")[0].trim().toUpperCase();
  return size || null;
}

// SIZE is by item type first (the owner, 2026-09-30: "sizes should be first
// derived by item type"), so a pick is a type and a size: a 34 in BOTTOMS (a
// waist) and a 34 in TOPS (a tailoring size) are different picks
// ("bottoms:34"). The types are the catalog's own (utils/itemCategories:
// TOPS, BOTTOMS, SHOES, ACCESSORIES, read from the title, as SHOP BY reads
// them); a piece none of them names is OTHER.
const OTHER_SIZE_TYPE = "other";
const sizeTypeOf = (item) => categoryOf(item) || OTHER_SIZE_TYPE;
function sizeKeyOf(item) {
  const size = sizeChipOf(item.size);
  return size ? `${sizeTypeOf(item)}:${size}` : null;
}

// Chip order: numbers, smallest up; then letter sizes in their own order;
// then anything else, alphabetically.
const LETTER_SIZES = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];
function compareSizes(a, b) {
  const na = /^\d+(\.\d+)?$/.test(a);
  const nb = /^\d+(\.\d+)?$/.test(b);
  if (na && nb) return Number(a) - Number(b);
  if (na !== nb) return na ? -1 : 1;
  const la = LETTER_SIZES.indexOf(a);
  const lb = LETTER_SIZES.indexOf(b);
  if (la !== -1 && lb !== -1) return la - lb;
  if ((la !== -1) !== (lb !== -1)) return la !== -1 ? -1 : 1;
  return a.localeCompare(b);
}

// The small check beside a picked option in the FILTER panel.
function PickedMark() {
  return (
    <svg className="featured-filter-panel__check" width="12" height="10" viewBox="0 0 12 10" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M1 5.2L4.3 8.5L11 1.5" />
    </svg>
  );
}

// How long after mount the catalog title stays non-sticky. Covers the longest
// arrival animation: the catalog fade-in (500ms) and the slot rise-in (450ms).
// See the .featured-desktop-title--settled rule in app.css.
const TITLE_SETTLE_MS = 600;
// First-visit peek on the title (see the peek effect below). The schedule
// is a list of waits: the first is measured from the catalog opening, each
// later one from the moment the previous peek fired. So [3000, 6000] peeks
// at 3s and again at 9s. PEEK_MS is how long one peek takes. Latched per
// page load so the sequence runs once.
const PEEK_SCHEDULE_MS = [3000, 6000];
const PEEK_MS = 1400;
let peekShownThisLoad = false;

const TITLE_PAGES = [
  { key: "featured", label: "Featured", path: "/featured" },
  { key: "new", label: "New Arrivals", path: "/featured/new" },
  { key: "sold", label: "Sold", path: "/featured/sold" },
];

// Track whether this is a fresh page load (survives re-renders, resets on full reload)
let _isFirstMount = true;

// FEATURED's own order, a random assortment (the owner, 2026-10-17: "featured
// maybe make it random assortment"). Each piece draws its place once per page
// load, so the order holds while browsing (back from a piece, a filter, a
// page and back) and a reload deals a fresh one. Pieces that arrive later
// draw theirs as they come.
const _featuredRank = new Map();
const featuredRankOf = (item) => {
  const key = item.id ?? item.title;
  if (!_featuredRank.has(key)) _featuredRank.set(key, Math.random());
  return _featuredRank.get(key);
};
// FEATURED's sold pieces: those at this price and up first, then the rest,
// each lot in its random order.
const FEATURED_SOLD_TIER_USD = 500;
// The catalog view the sort was last set up for: FEATURED takes its own
// order on arrival from another view (or on the first load), not on a
// return from a piece, so a NEWEST picked there is kept.
let _lastSortView = null;

// Module-level cache keyed by viewMode so switching tabs doesn't re-fetch
const _cache = {
  featured: { items: null, popularRanking: null, timestamp: 0 },
  new:      { items: null, popularRanking: null, timestamp: 0 },
  sold:     { items: null, popularRanking: null, timestamp: 0 },
};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Invalidate all caches (call when listings are edited/changed)
export function invalidateListingsCache() {
  for (const key of Object.keys(_cache)) {
    _cache[key] = { items: null, popularRanking: null, timestamp: 0 };
  }
}

// The catalog's data: live and sold together (the FILTER panel's
// availability picks between them), showcase pieces left to /showcase.
// Shared by the page's own load and preloadCatalog.
async function fetchCatalog() {
  const res = await dummyFetch("/api/inventory/?show_sold=true&limit=200");
  if (!res.ok) throw new Error(`Failed to fetch featured: ${res.status}`);
  const data = await res.json();
  const raw = Array.isArray(data) ? data : data.items || [];
  return raw.filter((item) => (item.state === "LIVE" || item.state === "SOLD") && !item.showcase);
}

// Fills the catalog's cache from elsewhere, so the catalog paints from it at
// once when it opens: the home search's rule fills while this runs and
// moves on when it is done (utils/searchFill). One request at a time; a
// fresh cache is left alone.
let _preloading = null;
export function preloadCatalog() {
  const cached = _cache.featured;
  if (cached.items && Date.now() - cached.timestamp < CACHE_TTL) return Promise.resolve();
  if (!_preloading) {
    _preloading = fetchCatalog()
      .then((items) => {
        _cache.featured = { items, popularRanking: null, timestamp: Date.now() };
      })
      .finally(() => {
        _preloading = null;
      });
  }
  return _preloading;
}

export default function FeaturedPage({ viewMode = "featured", searchTerm = "" }) {
  const [initialLoad] = useState(() => {
    // Back from a piece or the cart by their arrows (state.catalogEntrance):
    // the catalog comes in as it does on its first load (the owner,
    // 2026-10-02). React Router keeps navigation state under history.state.usr.
    if (typeof window !== "undefined" && window.history.state?.usr?.catalogEntrance) { _isFirstMount = false; return true; }
    if (_isFirstMount && viewMode === "featured") { _isFirstMount = false; return true; }
    if (_isFirstMount && viewMode !== "featured") { _isFirstMount = false; }
    return false;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading: authLoading } = useAuth();
  const { setCollapsed, setLoading } = useHeader();
  const isMobile = useIsMobile();
  // The desktop title wheel steps by one row of the big type size (see
  // useTitleRowPx), which follows the window width.
  const titleRowPx = useTitleRowPx();
  // Every catalog page loads the same list, live and sold together, since
  // availability is a filter now; one cache serves them all.
  const viewCache = _cache.featured;
  const [items, setItems] = useState(() => viewCache.items || []);
  const [featuredLoading, setFeaturedLoading] = useState(() => !viewCache.items);
  // Availability, from the address rather than held: the SOLD page is SOLD,
  // ?status=all is ALL, anything else AVAILABLE (the default). So the address
  // bar, the title and the panel cannot disagree (pickAvailability below).
  const statusParam = searchParams.get("status");
  // A search (/featured/search?q=) has no SOLD page of its own, so there SOLD
  // is ?status=sold (pickAvailability).
  const onSearchRoute = location.pathname.startsWith("/featured/search");
  // FEATURED opens on ALL (the owner, 2026-10-17: "on featured page, the
  // availability we should show is all so show it as default"), AVAILABLE
  // being ?status=available there. NEW ARRIVALS opens on AVAILABLE (the
  // owner, 2026-10-19: "for new arrivals available only"; it opened on ALL
  // for two days), as a search does, ALL being ?status=all.
  const featuredOpensAll = viewMode === "featured" && !onSearchRoute;
  const filterOption = viewMode === "sold" || (onSearchRoute && statusParam === "sold")
    ? "sold"
    : statusParam === "all" ? "all"
      : statusParam === "available" ? "available"
        : featuredOpensAll ? "all" : "available";
  // SIZE and BRAND picked in the FILTER panel: a piece matching any of the
  // picked sizes (brands) stays.
  const [sizeFilter, setSizeFilter] = useState([]);
  // The SIZE section's type rows that are open (keys, "tops" and so on).
  const [openSizeTypes, setOpenSizeTypes] = useState([]);
  const [brandFilter, setBrandFilter] = useState([]);
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  // The FILTER panel that is open: "filter" (the desktop bar) or
  // "filter-mobile" (the phone count bar), else null.
  const [openFilter, setOpenFilter] = useState(null);
  const [inlineSearchOpen, setInlineSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const inlineSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const filterBarRef = useRef(null);
  // The title's own wrapper inside that row — the wheel's zone (see the
  // window-level wheel capture below).
  const titleHoverRef = useRef(null);
  // The two FILTER controls (the desktop bar's, the phone count bar's): a
  // press outside the open one closes it.
  const filterRef = useRef(null);
  const mobileFilterRef = useRef(null);

  // Which grid row the viewport is on, out of how many. Measured from the
  // grid rather than tracked in state as items render: the column count is
  // whatever the CSS resolved to at this width, and reading it back is the
  // only way to stay right across the breakpoints.
  const gridRef = useRef(null);
  const titleSlotRef = useRef(null);
  const mobileTitleSlotRef = useRef(null);
  const sentinelRef = useRef(null);
  const [stuck, setStuck] = useState(false);

  // Sync stuck state to global header collapsed. Desktop only: there the
  // stuck title bar takes the header's place. On a phone the header keeps
  // its own hide-on-scroll-down / show-on-scroll-up rhythm and the stuck
  // title rides beneath it, so the menu, search and cart are one small
  // upward scroll away anywhere in the grid instead of at the very top.
  useEffect(() => {
    setCollapsed(stuck && !isMobile);
  }, [stuck, isMobile, setCollapsed]);

  // Sync loading state to header loading bar
  useEffect(() => {
    setLoading(featuredLoading);
  }, [featuredLoading, setLoading]);

  // Reset header on unmount
  useEffect(() => {
    return () => { setCollapsed(false); setLoading(false); };
  }, [setCollapsed, setLoading]);

  const searchContext = useFeaturedSearch();
  const searchOpen = searchContext?.searchOpen ?? false;
  const searchQuery = searchContext?.searchQuery ?? "";
  // (The filter bar's magnifier used to turn into a clear (x) once a query
  // was typed into its own small field beside it. That field is gone: the
  // title is the search field now (searchMode, below), and the one clear is
  // the x beside it.)
  const setSearchQuery = searchContext?.setSearchQuery ?? (() => {});
  const sortOption = searchContext?.sortOption ?? "newest";
  const setSortOption = searchContext?.setSortOption ?? (() => {});
  const closeSearch = searchContext?.closeSearch;

  // Debounced "committed" query — updates title 1s after user stops typing
  const [committedQuery, setCommittedQuery] = useState(searchTerm || "");

  // On a search page the address follows the search field (the owner,
  // 2026-10-16: the page and the address went out of step). A query typed
  // there is written into ?q= once it commits, in place (replace, and
  // keepSearchPage so App's FeaturedSearchRoute keeps this page mounted);
  // the searchTerm effects below then skip that write, so the field is not
  // reset under the reader (a trailing space mid-word would be trimmed).
  const selfWrittenQ = useRef(null);
  const writeQRef = useRef(null);
  writeQRef.current = (q) => {
    if (!location.pathname.startsWith("/featured/search") || q === (searchTerm || "")) return;
    const params = new URLSearchParams(searchParams);
    params.set("q", q);
    selfWrittenQ.current = q;
    navigate(`/featured/search?${params.toString()}`, { replace: true, state: { keepSearchPage: true } });
  };

  // Sync when searchTerm prop changes (e.g. navigating from search to catalog)
  useEffect(() => {
    if (searchTerm && searchTerm === selfWrittenQ.current) return;
    setCommittedQuery(searchTerm || "");
    setDisplayQuery(searchTerm || "");
    setSearchQuery(searchTerm || "");
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps
  const commitTimer = useRef(null);
  // Fade transition between search query and page title
  const [titleFade, setTitleFade] = useState("in"); // "in" | "out"
  const [displayQuery, setDisplayQuery] = useState(searchTerm || "");
  const fadeTimer = useRef(null);
  useEffect(() => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setCommittedQuery("");
      return;
    }
    commitTimer.current = setTimeout(() => {
      setCommittedQuery(trimmed);
      writeQRef.current?.(trimmed);
    }, 1000);
    return () => clearTimeout(commitTimer.current);
  }, [searchQuery]);

  // Handle transition when committedQuery changes — immediate swap both ways
  useEffect(() => {
    setDisplayQuery(committedQuery);
    setTitleFade("in");
  }, [committedQuery]);

  // ── The title is the search field ──
  // While a search is on, the page title IS the search field, as the home
  // page's search is (the owner, 2026-10-16: "make the search query what the
  // featured header is and the line into the search bar"). The magnifier, a
  // query arriving from elsewhere (the sidebar, the header, a results link),
  // or one being typed puts it in the title's place: the title's own face and
  // size, the white blinking caret, SEARCH in dark grey while empty. The rule
  // under the title is its line, left in its usual grey (the owner,
  // 2026-10-16: "dont make the line white"; it brightened while searching).
  // The FEATURED / NEW / SOLD wheel steps aside for it, and its arrows and
  // its peek with it. Pressing away from an empty field gives the title back.
  const searchMode = inlineSearchOpen || searchQuery.length > 0 || Boolean(committedQuery);
  // The desktop title's search field (the phone's sits in mobileSearchRef's
  // row): a press inside either does not count as pressing away.
  const titleSearchRef = useRef(null);
  // Focus whichever title field is on screen (desktop or phone). Tried a few
  // times: when search is asked for from another section, the page is still
  // mounting under its transition when the request lands.
  const focusTitleSearch = useCallback(() => {
    let tries = 0;
    const focus = () => {
      const input = [...document.querySelectorAll(".featured-title-search__input")]
        .find((el) => el.getClientRects().length > 0);
      if (input && document.activeElement !== input) input.focus();
      if ((!input || document.activeElement !== input) && ++tries < 10) setTimeout(focus, 80);
    };
    setTimeout(focus, 0);
  }, []);

  // Pre-populate search from searchTerm prop (search page route)
  useEffect(() => {
    if (searchTerm && searchTerm === selfWrittenQ.current) return;
    setSearchQuery(searchTerm || "");
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate search from navigation (drawer search, brand carousel, etc.)
  const incomingBrand = location.state?.brandSearch;
  useEffect(() => {
    if (incomingBrand) {
      setSearchQuery(incomingBrand);
      setInlineSearchOpen(true);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [incomingBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!inlineSearchOpen) return;
    const handleClickOutside = (e) => {
      const inDesktop = inlineSearchRef.current?.contains(e.target)
        || titleSearchRef.current?.contains(e.target);
      const inMobile = mobileSearchRef.current?.contains(e.target);
      if (!inDesktop && !inMobile) {
        setInlineSearchOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [inlineSearchOpen, setSearchQuery]);

  // Detect when filter bar becomes stuck
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: "0px 0px 0px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [featuredLoading]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    let cancelled = false;
    const cached = _cache.featured;
    const isFresh = cached.items && (Date.now() - cached.timestamp < CACHE_TTL);

    // Use cached data if fresh
    if (isFresh) {
      setItems(cached.items);
      setFeaturedLoading(false);
      return;
    }

    async function loadFeatured() {
      setFeaturedLoading(true);
      try {
        // Live and sold together, showcase pieces left out (fetchCatalog).
        const payload = await fetchCatalog();
        if (cancelled) return;

        _cache.featured = { items: payload, popularRanking: null, timestamp: Date.now() };
        setItems(payload);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load featured:", error);
        setItems([]);
      } finally {
        if (!cancelled) {
          setFeaturedLoading(false);
        }
      }
    }

    loadFeatured();
    return () => { cancelled = true; };
  }, [authLoading, viewMode]);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Header search icon (desktop): open the inline search box and focus it,
  // both when the request arrives while mounted and when it was raised just
  // before this page mounted (see utils/catalogSearchSignal).
  useEffect(() => {
    const open = () => {
      // The title turns into the search field and takes the caret
      // (focusTitleSearch keeps trying while the page is still mounting).
      setInlineSearchOpen(true);
      focusTitleSearch();
    };
    if (consumeCatalogSearchRequest()) open();
    return onCatalogSearchRequest(() => {
      consumeCatalogSearchRequest();
      open();
    });
  }, []);

  useEffect(() => {
    if (!searchOpen) {
      return;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && closeSearch) {
        closeSearch();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [searchOpen, closeSearch]);

  // The order: a link's ?sort= when it carries one; otherwise whatever the
  // reader last picked, which the search context keeps for the visit
  // (NEWEST to begin with). NEW ARRIVALS opens newest first, its name; keyed
  // on the page alone, so an availability change there keeps the pick.
  useEffect(() => {
    const sort = searchParams.get("sort");
    if (sort && SORT_OPTIONS.some((o) => o.value === sort)) setSortOption(sort);
  }, [searchParams, setSortOption]);

  useEffect(() => {
    if (viewMode === "new") setSortOption("newest");
  }, [viewMode, setSortOption]);

  // FEATURED (not a search on it) opens in its random assortment, and offers
  // it in SORT BY as FEATURED; elsewhere that pick reads as NEWEST. Before
  // paint, so the grid never shows a frame in the other order.
  const featuredOrder = viewMode === "featured" && !onSearchRoute;
  const sortOptions = useMemo(
    () => (featuredOrder ? [{ value: "featured", label: "Featured" }, ...SORT_OPTIONS] : SORT_OPTIONS),
    [featuredOrder],
  );
  const pageSort = featuredOrder ? "featured" : "newest";
  const effectiveSort = sortOption === "featured" && !featuredOrder ? "newest" : sortOption;
  useLayoutEffect(() => {
    const sortView = featuredOrder ? "featured" : onSearchRoute ? "search" : viewMode;
    if (featuredOrder && _lastSortView !== "featured" && !searchParams.get("sort")) {
      setSortOption((current) => (current === "newest" ? "featured" : current));
    }
    _lastSortView = sortView;
  }, [featuredOrder, onSearchRoute, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the FILTER panel on a press outside it, or Escape.
  useEffect(() => {
    if (!openFilter) return undefined;
    const handleClickOutside = (event) => {
      const inDesktop = filterRef.current?.contains(event.target);
      const inMobile = mobileFilterRef.current?.contains(event.target);
      if (!inDesktop && !inMobile) {
        setOpenFilter(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpenFilter(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openFilter]);

  // AVAILABLE / ALL / SOLD moves between the catalog's own addresses, so the
  // title keeps naming what is on screen: SOLD is /featured/sold; AVAILABLE
  // and ALL are the page the reader was on (FEATURED or NEW ARRIVALS), ALL
  // with ?status=all. Other params (a SHOP BY ?cat=) ride along, and the
  // pages share this component, so the sort, sizes, brands and price stay.
  // A search stays on its own address: ?q= is only read there, and carried
  // to /featured it left the address on the search while the page showed
  // FEATURED (the owner, 2026-10-16).
  const pickAvailability = (value) => {
    if (value === filterOption) return;
    const params = new URLSearchParams(searchParams);
    params.delete("status");
    // A query typed into the bar's field on a catalog page makes it a search
    // too: it goes to the search's address, with this availability (a
    // search opens on AVAILABLE).
    if (onSearchRoute || committedQuery) {
      if (committedQuery) params.set("q", committedQuery);
      if (value !== "available") params.set("status", value);
      navigate(`/featured/search?${params.toString()}`, onSearchRoute ? { state: { keepSearchPage: true } } : undefined);
      return;
    }
    const base = value === "sold" ? "/featured/sold" : viewMode === "new" ? "/featured/new" : "/featured";
    // A page's own availability needs no ?status=: FEATURED's is ALL, NEW
    // ARRIVALS' AVAILABLE, SOLD's its address.
    const own = base === "/featured" ? "all" : "available";
    if (value !== "sold" && value !== own) params.set("status", value);
    const qs = params.toString();
    navigate(qs ? `${base}?${qs}` : base);
  };

  // Emptying the search field on a search page leaves the search, for the
  // catalog page with the same availability, so the address never names a
  // search the page has dropped.
  const leaveEmptySearch = () => {
    if (!onSearchRoute) return;
    const status = searchParams.get("status");
    // FEATURED opens on ALL, so a search on AVAILABLE lands on
    // ?status=available there.
    navigate(status === "sold" ? "/featured/sold" : status === "all" ? "/featured" : "/featured?status=available");
  };

  // A ?q= on any other catalog page (an old link, or one made before the fix
  // above) goes to the search it names, so the page and the address agree.
  useEffect(() => {
    if (onSearchRoute || !searchParams.get("q")) return;
    const params = new URLSearchParams(searchParams);
    if (viewMode === "sold") params.set("status", "sold");
    navigate(`/featured/search?${params.toString()}${location.hash}`, { replace: true });
  }, [onSearchRoute, searchParams, viewMode, location.hash, navigate]);

  // The pieces the current availability admits, before size, brand, price
  // and search narrow them. The SIZE and BRAND choices and the price
  // placeholder come from these, so they only offer what can be found. A
  // picked chip stays listed even when an availability change leaves nothing
  // in it, so it can still be seen and cleared.
  const availabilityItems = useMemo(() => items.filter((item) => {
    const sold = isSoldOutItem(item);
    return filterOption === "all" || (filterOption === "sold" ? sold : !sold);
  }), [items, filterOption]);
  // SIZE, by item type: a row per type with stock, in the catalog's type
  // order with OTHER last, each holding that type's sizes (sorted as the
  // chips always were). A picked size stays listed even when an
  // availability change leaves nothing in it, so it can still be cleared.
  const sizeGroups = useMemo(() => {
    const byType = new Map();
    const add = (type, size) => {
      if (!byType.has(type)) byType.set(type, new Set());
      byType.get(type).add(size);
    };
    availabilityItems.forEach((item) => {
      const size = sizeChipOf(item.size);
      if (size) add(sizeTypeOf(item), size);
    });
    sizeFilter.forEach((pick) => {
      const at = pick.indexOf(":");
      if (at > 0) add(pick.slice(0, at), pick.slice(at + 1));
    });
    return [...CATEGORY_GROUPS.map((g) => g.key), OTHER_SIZE_TYPE]
      .filter((type) => byType.has(type))
      .map((type) => ({
        key: type,
        label: CATEGORY_BY_KEY[type]?.label || "Other",
        sizes: [...byType.get(type)].sort(compareSizes),
      }));
  }, [availabilityItems, sizeFilter]);
  // Opening the panel opens the type rows that hold a pick, so what is
  // picked is in view; the rest start closed.
  useEffect(() => {
    if (!openFilter) return;
    setOpenSizeTypes([...new Set(sizeFilter.map((pick) => pick.slice(0, pick.indexOf(":"))))]);
  }, [openFilter]);
  // The picks as the crumb over the title names them: type, then size.
  // The picks as the line over the title reads them: "SIZE 1, 44", the sizes
  // alone (the owner, 2026-10-02), each once.
  const sizeCrumb = `Size ${[...new Set(sizeFilter.map((pick) => pick.slice(pick.indexOf(":") + 1)))].join(", ")}`;
  const toggleSizeType = (type) => setOpenSizeTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  const brandOptions = useMemo(
    () => [...new Set([...availabilityItems.map(brandOf).filter(Boolean), ...brandFilter])].sort((a, b) => a.localeCompare(b)),
    [availabilityItems, brandFilter],
  );

  const highestPrice = useMemo(() => {
    let max = 0;
    availabilityItems.forEach((item) => {
      if (item.price_usd != null && item.price_usd > max) max = item.price_usd;
    });
    return max;
  }, [availabilityItems]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const isSearching = normalizedQuery.length > 0;
  // Category from the ?cat= param (the landing page's SHOP BY tiles link
  // here). Derived from titles, since the inventory's category field is
  // empty; see utils/itemCategories.js. An unknown key is ignored.
  // FEATURED only (the owner, 2026-10-02: "These filters can only be applied
  // on featured"): on NEW ARRIVALS and SOLD a ?cat= carried along (the
  // availability switch keeps the query) is ignored.
  const activeCategory = viewMode === "featured"
    ? CATEGORY_BY_KEY[searchParams.get("cat") || ""] || null
    : null;
  // With a category on, the FEATURED slot of the title names it instead:
  // TOPS, BOTTOMS, SHOES, ACCESSORIES. NEW ARRIVALS and SOLD keep theirs.
  // On the way out of a category (leaveCategory, below) the slot reads
  // FEATURED again before the page moves on.
  const [baseTitle, setBaseTitle] = useState(false);
  const titlePages = useMemo(
    () => (activeCategory && !baseTitle
      ? TITLE_PAGES.map((page) => (page.key === "featured" ? { ...page, label: activeCategory.label } : page))
      : TITLE_PAGES),
    [activeCategory, baseTitle],
  );

  const filteredItems = useMemo(() => {
    // The featured brands: the ones with a piece for sale, which is what the
    // showcase lists (utils/brandList reads the unsold inventory).
    const featuredBrands = new Set(items.filter((it) => !isSoldOutItem(it)).map((it) => brandOf(it).toLowerCase()));
    const parseDate = (value) => {
      const parsed = value ? Date.parse(value) : 0;
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const scored = items
      .map((item, index) => {
        const isSoldOut = item.sold || item.state !== "LIVE" || !item.is_available;

        if (filterOption === "available" && isSoldOut) return null;
        if (filterOption === "sold" && !isSoldOut) return null;
        if (sizeFilter.length && !sizeFilter.includes(sizeKeyOf(item))) return null;
        if (brandFilter.length && !brandFilter.includes(brandOf(item))) return null;

        // Price range filter
        const parsedFrom = priceFrom !== "" ? Number(priceFrom) : null;
        const parsedTo = priceTo !== "" ? Number(priceTo) : null;
        if (parsedFrom != null || parsedTo != null) {
          const price = item.price_usd ?? 0;
          if (parsedFrom != null && price < parsedFrom) return null;
          if (parsedTo != null && price > parsedTo) return null;
        }

        if (activeCategory && categoryOf(item) !== activeCategory.key) return null;

        let score = 0;
        if (isSearching) {
          const haystack = [
            item.title,
            item.brand_name || item.brand,
            item.category,
            item.style_code,
            item.sku,
            item.description,
            item.color,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          if (!haystack.includes(normalizedQuery)) {
            return null;
          }

          const title = (item.title || "").toLowerCase();
          if (title.startsWith(normalizedQuery)) score += 4;
          if (title.includes(normalizedQuery)) score += 3;

          const brand = (item.brand_name || item.brand || "").toLowerCase();
          if (brand.includes(normalizedQuery)) score += 2;

          const description = (item.description || "").toLowerCase();
          if (description.includes(normalizedQuery)) score += 1;

          const styleCode = (item.style_code || item.sku || "").toLowerCase();
          if (styleCode && normalizedQuery.length > 2 && styleCode.includes(normalizedQuery)) {
            score += 2;
          }
        }

        return { item, index, score };
      })
      .filter(Boolean);

    const sorted = scored.sort((a, b) => {
      // ALL: the pieces still for sale first, the sold after them, each
      // group in the chosen order (the owner, 2026-10-17: "show available
      // items first and then sold items after for all"). Not on NEW
      // ARRIVALS, which is strictly the order pieces went up, sold or not.
      if (filterOption === "all" && viewMode !== "new") {
        const soldA = isSoldOutItem(a.item) ? 1 : 0;
        const soldB = isSoldOutItem(b.item) ? 1 : 0;
        if (soldA !== soldB) return soldA - soldB;
      }
      if (isSearching && a.score !== b.score) {
        return b.score - a.score;
      }
      switch (effectiveSort) {
        case "featured": {
          // The sold pieces (which ALL lists after the rest), in three random
          // lots: the featured brands' at $500 and up, then other brands' at
          // $500 and up, then everything under $500 (the owner, 2026-10-17:
          // "these items must be from featured brands those come first"). The
          // pieces for sale stay one random mix.
          if (isSoldOutItem(a.item) && isSoldOutItem(b.item)) {
            const tier = (it) => {
              if ((it.price_usd ?? 0) < FEATURED_SOLD_TIER_USD) return 2;
              return featuredBrands.has(brandOf(it).toLowerCase()) ? 0 : 1;
            };
            const byTier = tier(a.item) - tier(b.item);
            if (byTier) return byTier;
          }
          return featuredRankOf(a.item) - featuredRankOf(b.item);
        }
        case "price-asc":
          return (a.item.price_usd ?? Infinity) - (b.item.price_usd ?? Infinity);
        case "price-desc":
          return (b.item.price_usd ?? -Infinity) - (a.item.price_usd ?? -Infinity);
        default:
          // NEWEST, the default: the latest listing first, the order the
          // pieces went up (and were posted); ties keep the API's order.
          return parseDate(b.item.created_at || b.item.updated_at) - parseDate(a.item.created_at || a.item.updated_at)
            || a.index - b.index;
      }
    });

    return sorted.map(({ item }) => item);
  }, [activeCategory, items, filterOption, sizeFilter, brandFilter, isSearching, normalizedQuery, effectiveSort, priceFrom, priceTo]);

  // ── Title page slot (vertical scroll between Featured / New / Sold) ──
  const currentPageIndex = TITLE_PAGES.findIndex((p) => p.key === viewMode);
  const [atTop, setAtTop] = useState(() => window.scrollY <= 0);

  // Whether there is anything below the screen to scroll to before the
  // footer, for the down caret (utils/scrollCue). Re-measured on scroll and
  // whenever the body resizes (items loading, a filter or search, a window
  // change), so the caret never offers a scroll that goes nowhere and never
  // sits over the footer's links.
  const [hasBelow, setHasBelow] = useState(false);
  // Whether the reader is, right now, scrolling DOWN. The up caret stays
  // out of the way while they are: someone still travelling down is doing
  // it on purpose and does not want a way back waved at them. It shows once
  // they turn back up, or simply stop (SCROLL_STILL_MS of no scroll events).
  const [movingDown, setMovingDown] = useState(false);

  // ── First-visit peek. Once per page load, on the PEEK_SCHEDULE_MS
  // schedule, the title slides two thirds of the way toward NEW ARRIVALS
  // and settles back with the down arrow lit (.slot-filter--peek-next in
  // app.css), so a newcomer learns the title is a wheel with more sections
  // in it. Only from FEATURED, only at the top of the page, only with no
  // search committed, all checked each time a step fires: if by then they
  // have scrolled, searched or moved on, that step and every later one are
  // skipped for this load, not deferred. The latch is set when the first
  // step fires, not when it is armed, so a mount that is torn down early
  // (StrictMode, a quick bounce away) cannot use up the one sequence. ──
  const [peeking, setPeeking] = useState(false);
  // A search counts from the moment the title becomes the field, not only
  // once a query commits: the wheel is not there to peek.
  const peekGuardRef = useRef({ viewMode, committedQuery });
  peekGuardRef.current = { viewMode, committedQuery: committedQuery || (searchMode ? "search" : "") };
  // The pending timers, in a ref so the section-change effect below can
  // sweep them.
  const peekTimersRef = useRef([]);
  useEffect(() => {
    // Each fresh arrival at the catalog re-arms the sequence. The latch's job
    // is to stop the peek repeating WITHIN a visit, not to spend it for the
    // life of the tab: it was set for the whole SPA session, and the
    // section-change effect below burns it the moment NEW ARRIVALS or SOLD is
    // opened — so in practice the peek was something you saw once and then
    // never again. Switching sections still ends it for that visit; coming
    // back to the catalog from another page starts it over.
    //
    // Safe against StrictMode's double mount: both mounts reset at mount time,
    // seconds before any step can fire, and the torn-down mount's timers are
    // swept by this effect's cleanup. Switching view modes does not remount
    // this component (same element slot, changed prop), so it is genuinely a
    // new arrival that re-arms, not a tab change.
    peekShownThisLoad = false;
    const timers = peekTimersRef.current;
    const runStep = (step) => {
      if (step >= PEEK_SCHEDULE_MS.length) return;
      timers.push(setTimeout(() => {
        if (step === 0) {
          if (peekShownThisLoad) return;
          peekShownThisLoad = true;
        }
        const { viewMode: mode, committedQuery: query } = peekGuardRef.current;
        if (mode !== "featured" || query || window.scrollY > 0) return;
        setPeeking(true);
        timers.push(setTimeout(() => setPeeking(false), PEEK_MS));
        // The next wait starts now, from the moment this peek fired.
        runStep(step + 1);
      }, PEEK_SCHEDULE_MS[step]));
    };
    runStep(0);
    return () => timers.forEach(clearTimeout);
  }, []);

  // Once the visitor has switched sections by any means (the title, the
  // arrows, the wheel), they know how it works: the sequence ends for this
  // load, a peek in flight stops, and coming back to FEATURED does not
  // revive it.
  useEffect(() => {
    if (viewMode === "featured") return;
    peekShownThisLoad = true;
    peekTimersRef.current.forEach(clearTimeout);
    peekTimersRef.current = [];
    setPeeking(false);
  }, [viewMode]);
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      setHasBelow(hasMoreBelow());
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(document.body);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // One press brings the next rows up: most of a screen, so the row that was
  // cut off at the fold lands in view with its top still visible.
  const handleScrollDown = useCallback(() => {
    window.scrollBy({ top: Math.round(window.innerHeight * 0.85), behavior: "smooth" });
  }, []);

  // The up caret, centred in the sticky title band: the back-to-top for a
  // reader who has scrolled. The desktop and mobile title rows each hold one
  // (only one row is displayed at a time). It is an absolute child of the
  // row, so it rides with the row and sits between the title and the sort
  // controls whatever the header is doing; handleTitleClick already owns the
  // smooth scroll and the scrollingToTop flag, so this shares it.
  const renderScrollUp = (shown) => (
    <button
      type="button"
      className={`featured-scroll-up${shown ? " featured-scroll-up--shown" : ""}`}
      aria-label="Back to top"
      tabIndex={shown ? 0 : -1}
      aria-hidden={!shown}
      onClick={handleTitleClick}
    >
      {/* The same thin line as the down caret (2px, round caps, as
          the arrows beside the title); this one holds still. */}
      <svg className="featured-scroll-caret__glyph" width="24" height="14" viewBox="0 0 14 8" fill="none" stroke="currentColor"
        strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M1 7L7 1L13 7" />
      </svg>
    </button>
  );

  useEffect(() => {
    const SCROLL_STILL_MS = 300;
    let lastY = window.scrollY;
    let stillTimer = null;
    const onScroll = () => {
      const y = window.scrollY;
      setAtTop(y <= 0);
      // Direction from the last event; a 1px dead band ignores jitter.
      if (y > lastY + 1) setMovingDown(true);
      else if (y < lastY - 1) setMovingDown(false);
      lastY = y;
      // No events for a moment means they have stopped: not moving down.
      clearTimeout(stillTimer);
      stillTimer = setTimeout(() => setMovingDown(false), SCROLL_STILL_MS);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(stillTimer);
    };
  }, []);

  // ── Leaving a category ──
  // Out of TOPS (or any category, or with sizes picked) for another catalog
  // page: the title and the size line fade out, the title fades back in as
  // FEATURED with the sizes cleared, and then the page navigates (the owner,
  // 2026-10-02). About half a second in all. The menu asks through a
  // cancelable "catalog-leave" event (Header handleMenuNavigate); a target
  // that is where we already are is left to the menu.
  const [crumbOut, setCrumbOut] = useState(false);
  const leaveTimer = useRef(0);
  useEffect(() => () => clearTimeout(leaveTimer.current), []);
  useEffect(() => {
    const onLeave = (e) => {
      const { path, state } = e.detail || {};
      if (!activeCategory && sizeFilter.length === 0) return;
      if (path === `${location.pathname}${location.search}`) return;
      e.preventDefault();
      clearTimeout(leaveTimer.current);
      setTitleFade("out");
      setCrumbOut(true);
      leaveTimer.current = setTimeout(() => {
        setBaseTitle(true);
        setSizeFilter([]);
        setTitleFade("in");
        leaveTimer.current = setTimeout(() => {
          navigate(path, state ? { state } : undefined);
          setBaseTitle(false);
          setCrumbOut(false);
        }, 220);
      }, 220);
    };
    window.addEventListener("catalog-leave", onLeave);
    return () => window.removeEventListener("catalog-leave", onLeave);
  }, [activeCategory, sizeFilter.length, location.pathname, location.search, navigate]);

  const handleTitleSlotChange = useCallback((index) => {
    const page = TITLE_PAGES[index];
    if (page) {
      navigate(page.path);
    }
  }, [navigate]);

  // Window-level wheel capture for the desktop title row.
  // Reasons this lives on window (not on the slot's own container):
  //   1. After viewMode navigation the FeaturedPage remounts; the slot DOM
  //      may shift slightly so the cursor is no longer over it. A
  //      window-level handler with manual hit-testing always catches the
  //      next gesture, so the user doesn't need to wiggle the mouse.
  //   2. Trackpad inertia past the slot's edges was leaking to the page.
  //      That used to be stopped by capturing the whole row's bbox, which
  //      also meant wheeling over the empty middle of the row, or over SORT
  //      BY, changed the section. The zone is now the title's visible text
  //      only, and the leak is stopped by ownership instead: a gesture that
  //      starts over the title keeps the wheel until it goes quiet, wherever
  //      the pointer is by then.
  useEffect(() => {
    if (committedQuery) return undefined;
    let lock = false;
    let idleTimer = null;
    let releaseTimer = null;
    let lastMouseMove = 0;
    const SILENCE_MS = 160;
    const SLIDE_MS = 370;
    const MOUSE_IDLE_MS = 1000;

    const onMouseMove = () => {
      lastMouseMove = performance.now();
    };

    // The zone is the title's visible text. The wrapper's own box is only
    // one row tall: the stack clips BELOW the active label but lets the other
    // labels hang ABOVE it (the grey FEATURED over NEW ARRIVALS), so every
    // label actually on screen is unioned in, with the arrows beside the
    // title. A label clipped away below the row is not text anyone can see,
    // and neither is one sitting under the site header, so the zone stops at
    // both. A few pixels of give, so the edge of a letter still counts.
    const ZONE_PAD = 8;
    const overTitle = (x, y) => {
      const wrap = titleHoverRef.current;
      if (!wrap) return false;
      const box = wrap.getBoundingClientRect();
      let { left, right, top } = box;
      const { bottom } = box;
      for (const node of wrap.querySelectorAll(".slot-filter__stack-item, .slot-filter__arrows")) {
        const r = node.getBoundingClientRect();
        if (!r.width || r.top >= bottom) continue;
        left = Math.min(left, r.left);
        right = Math.max(right, r.right);
        top = Math.min(top, r.top);
      }
      const header = document.querySelector(".app-header");
      if (header) top = Math.max(top, header.getBoundingClientRect().bottom);
      return x >= left - ZONE_PAD && x <= right + ZONE_PAD
        && y >= top - ZONE_PAD && y <= bottom + ZONE_PAD;
    };

    // Whether a gesture that began over the title still owns the wheel. Set
    // by the first event it claims; cleared by the same SILENCE_MS of quiet
    // that ends the gesture. Without it, a trackpad's inertia tail with the
    // pointer resting just off a letter would fall through to the page,
    // scroll it, and trip the scrollY guard below mid-slide.
    let owned = false;

    const onWheel = (e) => {
      if (!owned && !overTitle(e.clientX, e.clientY)) return;
      // Only claim the wheel at the very top of the page. Once the user has
      // scrolled down into the product grid, wheel events must pass through
      // to normal page scrolling so the grid can scroll.
      if (window.scrollY > 0) return;
      // And only once the cursor has rested over the title for MOUSE_IDLE_MS
      // — matches the header slot behaviour so stray mouse movement across it
      // doesn't hijack the page scroll. Checked on a gesture's first event
      // only: a hand that drifts while the gesture is under way has not
      // changed its mind.
      if (!owned && performance.now() - lastMouseMove < MOUSE_IDLE_MS) return;
      e.preventDefault();
      owned = true;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => { lock = false; owned = false; }, SILENCE_MS);
      if (lock) return;
      const dir = e.deltaY > 0 ? 1 : -1;
      const next = currentPageIndex + dir;
      if (next < 0 || next >= TITLE_PAGES.length) return;
      lock = true;
      titleSlotRef.current?.triggerSlide(dir);
      clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => { lock = false; }, SLIDE_MS);
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("wheel", onWheel);
      clearTimeout(idleTimer);
      clearTimeout(releaseTimer);
    };
  }, [currentPageIndex, committedQuery]);

  // Arriving here carries in the previous page's scroll offset — nothing in
  // the app resets it on a route change. That matters because the page title
  // is sticky: at a non-zero offset it pins under the site header while the
  // product count, which follows it in the document and is NOT sticky,
  // scrolls up past it. The count then renders ABOVE the title until
  // Header.jsx's own reset fires 160ms after navigating, which is exactly the
  // window the glitch was visible in.
  //
  // useLayoutEffect, not useEffect, so the wrong position is never painted.
  // Skipped on POP (browser back/forward), where the scroll position is the
  // one the visitor left behind and restoring it is correct.
  // The real type, from PageTransition: useNavigationType() here always
  // read POP, so both resets below were skipped on every arrival
  // (utils/appNavigationType).
  const navigationType = useAppNavigationType();
  useLayoutEffect(() => {
    if (navigationType === "POP") return;
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Again, once, after the first list paints. On mount the page is still the
  // loading placeholder and barely taller than the viewport, so the scroll
  // above has almost nothing to travel — arriving from a scrolled home page
  // (the SHOP BY tiles sit near its bottom) then left the reader partway down
  // the grid as soon as the products arrived and the document grew.
  // Latched with a ref so a later background refresh cannot yank someone who
  // has since scrolled back to the top, and skipped on POP for the same
  // reason as above: that position is the one they left behind.
  const didLoadScroll = useRef(false);
  useEffect(() => {
    if (navigationType === "POP" || featuredLoading || didLoadScroll.current) return;
    didLoadScroll.current = true;
    window.scrollTo(0, 0);
  }, [featuredLoading, navigationType]);

  // Re-sorting reorders the whole grid under the reader, so it starts again
  // from the top. Neither path here resets it on its own: picking a sort
  // either sets sortOption in place, or navigates between /featured,
  // /featured/new and /featured/sold — which keeps this same component
  // mounted, so the mount effect above never runs.
  // Instant, not smooth: the list has already changed, and animating back up
  // past items that are no longer the ones you were looking at is worse than
  // simply being at the top.
  const lastSort = useRef(sortOption);
  useEffect(() => {
    if (lastSort.current === sortOption) return;
    lastSort.current = sortOption;
    window.scrollTo(0, 0);
  }, [sortOption]);

  // The title is not sticky until the arrival animations are over: nothing can
  // push a static element, so the count beneath it cannot paint above it on
  // arrival, and it still scrolls away afterwards. See app.css.
  const [titleSettled, setTitleSettled] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setTitleSettled(true), TITLE_SETTLE_MS);
    return () => clearTimeout(id);
  }, []);

  const [scrollingToTop, setScrollingToTop] = useState(false);

  const handleTitleClick = useCallback(() => {
    if (window.scrollY <= 0) return;
    setScrollingToTop(true);
    window.scrollTo({ top: 0, behavior: "smooth" });

    const checkDone = () => {
      if (window.scrollY <= 0) {
        setScrollingToTop(false);
      } else {
        requestAnimationFrame(checkDone);
      }
    };
    requestAnimationFrame(checkDone);
  }, []);

  // At the top of the catalog a tap on the title turns the wheel one section
  // on (FEATURED -> NEW ARRIVALS -> SOLD -> round again): the same slide the
  // down arrow beside it makes, which lands and then navigates. It used to
  // go home; HOME has its own link now, and the peek has just shown that the
  // title is a wheel with more in it. Both title rows are in the DOM (one is
  // display: none), so the slide goes to the one on screen; if neither ref
  // is up yet, navigate straight to the next section instead.
  // Arrived from the menu's NEW ARRIVALS (Header, state.slideTo "new"): the
  // catalog opens on FEATURED and, once it has settled, turns its title wheel
  // on to NEW ARRIVALS, the slide the down arrow makes, which then navigates.
  // Once per arrival (keyed on the location), so going back does not replay it.
  const slidToKey = useRef(null);
  useEffect(() => {
    if (location.state?.slideTo !== "new" || viewMode !== "featured" || featuredLoading) return undefined;
    if (slidToKey.current === location.key) return undefined;
    const t = setTimeout(() => {
      slidToKey.current = location.key;
      const slot = (isMobileViewport() ? mobileTitleSlotRef.current : titleSlotRef.current)
        || titleSlotRef.current || mobileTitleSlotRef.current;
      // Half the wheel's usual 350ms (the owner: "transition in like half the time").
      if (slot && typeof slot.triggerSlide === "function") slot.triggerSlide(1, 175);
      else navigate("/featured/new");
    }, 250);
    return () => clearTimeout(t);
  }, [location.key, location.state, viewMode, featuredLoading, navigate]);

  const handleTitleNext = useCallback(() => {
    const onPhone = isMobileViewport();
    const slot = (onPhone ? mobileTitleSlotRef.current : titleSlotRef.current)
      || titleSlotRef.current || mobileTitleSlotRef.current;
    if (slot && typeof slot.triggerSlide === "function") {
      slot.triggerSlide(1);
      return;
    }
    handleTitleSlotChange((Math.max(0, currentPageIndex) + 1) % TITLE_PAGES.length);
  }, [currentPageIndex, handleTitleSlotChange]);

  // The title always reads as the title. Back-to-top is advertised by its own
  // caret parked in the page's bottom-left corner (below) rather than by
  // borrowing the title's slot, which meant the header showed a symbol
  // instead of where you were.
  const titleFilters = useMemo(() => {
    if (displayQuery) return [{ key: "search", label: `"${displayQuery}"` }];
    return titlePages;
  }, [displayQuery, titlePages]);

  const productCount = filteredItems.length;

  // ── The grid follows the filters with motion ──
  // When the FILTER panel's choices change (the owner, 2026-09-30: "when
  // size is selected fade out others and slide applicable pieces in their
  // places"), the pieces that no longer match fade out where they stand
  // (GRID_EXIT_MS), then the grid takes its new shape and every piece that
  // stays slides from its old place to its new one (a FLIP: measured before
  // and after, played back as a transform, GRID_SLIDE_MS). New pieces fade
  // in where they land, without the first load's stagger. Only transform and
  // opacity move, so a phone's two columns stay smooth. The grid holds its
  // height until the slide ends, so a shorter result cannot pull the page up
  // mid-slide, and the browser's scroll anchoring is off while it moves (it
  // threw the page 18,000px down when an unpick put 97 pieces back above the
  // one in view). Made from inside the grid, scrolled past its top, a change
  // takes the page smoothly back to the results' start, just under the
  // stuck title bar, while the fade runs, and slides from there: the results
  // read from the top, the open panel stays put, and nothing is left to
  // clamp the page when the height lets go. A new choice mid-move
  // starts over from where things are. A
  // search, another catalog page or reduced motion takes the new grid at
  // once. The panel over the grid takes its pointer the whole time, so
  // nothing here needs it.
  const GRID_EXIT_MS = 180;
  const GRID_SLIDE_MS = 420;
  const [displayItems, setDisplayItems] = useState(filteredItems);
  const [exitingIds, setExitingIds] = useState(() => new Set());
  const gridMotion = useRef({ timers: [], flipFrom: null });
  const filterSignature = [sortOption, filterOption, sizeFilter.join(","), brandFilter.join(","), priceFrom, priceTo].join("|");
  const contextSignature = [viewMode, normalizedQuery, activeCategory ? activeCategory.key : ""].join("|");
  const lastSignatures = useRef({ filter: filterSignature, context: contextSignature });
  // The first load's reveal staggers the cards; pieces a filter brings in
  // later fade in together. Staggered again on each catalog page.
  const revealStagger = useRef(true);
  useEffect(() => {
    revealStagger.current = true;
    const t = setTimeout(() => { revealStagger.current = false; }, 1500);
    return () => clearTimeout(t);
  }, [viewMode]);
  const cardIdOf = (card) => (card.getAttribute("href") || "").split("/").pop();
  const clearCardMotion = () => {
    gridRef.current?.querySelectorAll(".product-card").forEach((card) => {
      card.style.transition = "";
      card.style.transform = "";
    });
  };
  useEffect(() => {
    const motion = gridMotion.current;
    const prev = lastSignatures.current;
    const filtersChanged = prev.filter !== filterSignature;
    const contextChanged = prev.context !== contextSignature;
    lastSignatures.current = { filter: filterSignature, context: contextSignature };
    const same = displayItems.length === filteredItems.length && displayItems.every((item, i) => item.id === filteredItems[i].id);
    if (same) {
      if (displayItems !== filteredItems) setDisplayItems(filteredItems);
      return;
    }
    motion.timers.forEach((t) => (typeof t === "object" ? cancelAnimationFrame(t.raf) : clearTimeout(t)));
    motion.timers = [];
    motion.flipFrom = null;
    motion.token = (motion.token || 0) + 1;
    const token = motion.token;
    clearCardMotion();
    const grid = gridRef.current;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Scrolled into the grid: its top is under the title bar (stuck there
    // on desktop) or above the screen. The results' start is then brought
    // back to just under the bar, where the bar stays stuck, so the open
    // FILTER panel it carries does not move under the pointer.
    const bar = document.querySelector(".featured-desktop-title");
    const barBottom = bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
    const gridTop = grid ? grid.getBoundingClientRect().top : 0;
    const scrollTarget = Math.max(0, Math.round(gridTop + window.scrollY - barBottom - 16));
    const scrolledIn = Boolean(grid) && gridTop < barBottom && window.scrollY > scrollTarget + 2;
    if (!filtersChanged || contextChanged || reduce || !grid || !displayItems.length) {
      if (grid) grid.style.minHeight = "";
      document.documentElement.style.overflowAnchor = "";
      if (filtersChanged && !contextChanged && scrolledIn) window.scrollTo(0, scrollTarget);
      setExitingIds(new Set());
      setDisplayItems(filteredItems);
      return;
    }
    document.documentElement.style.overflowAnchor = "none";
    const staying = new Set(filteredItems.map((item) => item.id));
    const leaving = displayItems.filter((item) => !staying.has(item.id)).map((item) => item.id);
    const exited = leaving.length
      ? new Promise((resolve) => { motion.timers.push(setTimeout(resolve, GRID_EXIT_MS)); })
      : Promise.resolve();
    // Back to the results' start, smoothly, when the change was made from
    // inside the grid; settled once there (or after 900ms regardless).
    const scrolled = scrolledIn
      ? new Promise((resolve) => {
        window.scrollTo({ top: scrollTarget, behavior: "smooth" });
        const started = performance.now();
        const check = () => {
          if (motion.token !== token || Math.abs(window.scrollY - scrollTarget) <= 1 || performance.now() - started > 900) {
            resolve();
            return;
          }
          motion.timers.push({ raf: requestAnimationFrame(check) });
        };
        motion.timers.push({ raf: requestAnimationFrame(check) });
      })
      : Promise.resolve();
    const commit = () => {
      const from = new Map();
      grid.querySelectorAll(".product-card").forEach((card) => from.set(cardIdOf(card), card.getBoundingClientRect()));
      motion.flipFrom = from;
      grid.style.minHeight = `${grid.offsetHeight}px`;
      setExitingIds(new Set());
      setDisplayItems(filteredItems);
    };
    if (leaving.length) setExitingIds(new Set(leaving));
    Promise.all([exited, scrolled]).then(() => {
      if (motion.token === token) commit();
    });
  }, [filteredItems]);
  // The slide: each staying piece starts at its old place, as a transform,
  // and eases to its new one.
  useLayoutEffect(() => {
    const motion = gridMotion.current;
    const from = motion.flipFrom;
    const grid = gridRef.current;
    if (!from || !grid) return;
    motion.flipFrom = null;
    const moved = [];
    grid.querySelectorAll(".product-card").forEach((card) => {
      const was = from.get(cardIdOf(card));
      if (!was) return;
      const now = card.getBoundingClientRect();
      const dx = was.left - now.left;
      const dy = was.top - now.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
      card.style.transition = "none";
      card.style.transform = `translate(${dx}px, ${dy}px)`;
      moved.push(card);
    });
    grid.getBoundingClientRect();
    const raf = requestAnimationFrame(() => {
      moved.forEach((card) => {
        card.style.transition = `transform ${GRID_SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        card.style.transform = "";
      });
      motion.timers.push(setTimeout(() => {
        clearCardMotion();
        grid.style.minHeight = "";
        document.documentElement.style.overflowAnchor = "";
      }, GRID_SLIDE_MS + 60));
    });
    motion.timers.push({ raf });
  }, [displayItems]);
  useEffect(() => () => {
    gridMotion.current.timers.forEach((t) => (typeof t === "object" ? cancelAnimationFrame(t.raf) : clearTimeout(t)));
    gridMotion.current.token = (gridMotion.current.token || 0) + 1;
    document.documentElement.style.overflowAnchor = "";
  }, []);

  // Build active filter chips
  const activeChips = useMemo(() => {
    const chips = [];
    if (effectiveSort !== pageSort) {
      chips.push({
        key: "sort",
        label: sortOptions.find((o) => o.value === effectiveSort)?.label ?? effectiveSort,
        onRemove: () => setSortOption(pageSort),
      });
    }
    if (priceFrom !== "" || priceTo !== "") {
      const label = `$${priceFrom || "0"} – $${priceTo || "∞"}`;
      chips.push({
        key: "price",
        label,
        onRemove: () => { setPriceFrom(""); setPriceTo(""); },
      });
    }
    if (isSearching) {
      chips.push({
        key: "search",
        label: `"${searchQuery.trim()}"`,
        onRemove: () => setSearchQuery(""),
      });
    }
    return chips;
  }, [effectiveSort, pageSort, sortOptions, priceFrom, priceTo, isSearching, searchQuery, setSortOption, setSearchQuery]);

  // Picks an order from the FILTER panel: a re-sort in place, the panel left
  // open so the filters can be set in the same visit. (Orders used to double
  // as the catalog's pages; availability does that job now.)
  const pickSort = (value) => setSortOption(value);

  // FILTER: one word in the bar opens one panel, in the owner's order (spec of
  // 2026-09-30): SORT BY (with RESET at the top right), AVAILABILITY, SIZE,
  // BRAND, PRICE. The desktop bar and the phone count bar each render one,
  // the phone's hung from the count bar's right edge (styles/mobile.css).
  // The page's own settings are NEWEST and the availability the page opens
  // on (SOLD on the SOLD page, AVAILABLE elsewhere); the word lights, and
  // RESET wakes, when anything differs from them.
  const pageAvailability = viewMode === "sold" ? "sold" : featuredOpensAll ? "all" : "available";
  const filterActive = effectiveSort !== pageSort || filterOption !== pageAvailability
    || sizeFilter.length > 0 || brandFilter.length > 0 || priceFrom !== "" || priceTo !== "";
  // RESET: everything back to those settings, not just the price.
  const resetFilters = () => {
    setSortOption(pageSort);
    setSizeFilter([]);
    setBrandFilter([]);
    setPriceFrom("");
    setPriceTo("");
    if (filterOption !== pageAvailability) pickAvailability(pageAvailability);
  };
  const renderFilterControl = (instanceRef, key, mobile) => {
    const open = openFilter === key;
    const triggerClass = mobile ? "featured-count-bar__filter-btn" : "featured-filter-bar__trigger";
    return (
      <div className={`featured-filter-bar__filter${mobile ? " featured-filter-bar__filter--mobile-inline" : ""}`} ref={instanceRef}>
        <button
          type="button"
          className={`${triggerClass}${open ? ` ${triggerClass}--open` : ""}${filterActive ? ` ${triggerClass}--active` : ""}`}
          onClick={() => {
            setInlineSearchOpen(false);
            setOpenFilter((prev) => (prev === key ? null : key));
          }}
          aria-expanded={open}
          aria-controls={`featured-filter-panel-${key}`}
        >
          Filter
        </button>
        {open && (
          <div
            id={`featured-filter-panel-${key}`}
            className={`featured-filter-bar__dropdown featured-filter-panel${mobile ? " featured-filter-bar__dropdown--mobile-inline" : ""}`}
            role="group"
            aria-label="Sort and filter"
          >
            <div className="featured-filter-panel__section">
              <div className="featured-filter-panel__heading">
                <span id={`featured-filter-sort-${key}`}>Sort by</span>
                {/* RESET, top right: everything back to the page's own
                    settings (resetFilters), not just the price. */}
                <button
                  type="button"
                  className="price-filter__reset featured-filter-panel__reset"
                  onClick={resetFilters}
                  disabled={!filterActive}
                >
                  Reset
                </button>
              </div>
              <div className="featured-filter-panel__options" role="listbox" aria-labelledby={`featured-filter-sort-${key}`}>
                {sortOptions.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    role="option"
                    aria-selected={effectiveSort === value}
                    className={`featured-sort__option${effectiveSort === value ? " featured-sort__option--active" : ""}`}
                    onClick={() => pickSort(value)}
                  >
                    <span>{label}</span>
                    {effectiveSort === value && <PickedMark />}
                  </button>
                ))}
              </div>
            </div>
            <div className="featured-filter-panel__section">
              <div className="featured-filter-panel__heading" id={`featured-filter-availability-${key}`}>Availability</div>
              <div className="featured-filter-panel__toggle" role="radiogroup" aria-labelledby={`featured-filter-availability-${key}`}>
                {AVAILABILITY_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={filterOption === value}
                    className={`featured-filter-panel__toggle-option${filterOption === value ? " featured-filter-panel__toggle-option--active" : ""}`}
                    onClick={() => pickAvailability(value)}
                  >
                    {filterOption === value && <PickedMark />}
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>
            {sizeGroups.length > 0 && (
              <div className="featured-filter-panel__section">
                <div className="featured-filter-panel__heading" id={`featured-filter-size-${key}`}>Size</div>
                {/* By item type first: a row per type, which opens to that
                    type's sizes. A type holding a pick is white and names
                    its picks in grey before the chevron. */}
                <div className="featured-filter-panel__options" role="group" aria-labelledby={`featured-filter-size-${key}`}>
                  {sizeGroups.map((group) => {
                    const expanded = openSizeTypes.includes(group.key);
                    const picked = group.sizes.filter((size) => sizeFilter.includes(`${group.key}:${size}`));
                    const chipsId = `featured-filter-size-${key}-${group.key}`;
                    return (
                      <React.Fragment key={group.key}>
                        <button
                          type="button"
                          className={`featured-sort__option featured-filter-panel__type${picked.length ? " featured-sort__option--active" : ""}`}
                          aria-expanded={expanded}
                          aria-controls={chipsId}
                          onClick={() => toggleSizeType(group.key)}
                        >
                          <span>{group.label}</span>
                          <span className="featured-filter-panel__type-meta">
                            {picked.length > 0 && <span className="featured-filter-panel__type-picked">{picked.join(" ")}</span>}
                            <svg className="featured-filter-panel__type-chevron" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false">
                              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                        </button>
                        {expanded && (
                          <div className="featured-filter-panel__chips featured-filter-panel__chips--nested" id={chipsId} role="group" aria-label={`${group.label} sizes`}>
                            {/* SEE ALL first: lit while none of this type's sizes
                                is picked; pressed, it clears them (the owner,
                                2026-10-02). */}
                            <button
                              type="button"
                              aria-pressed={picked.length === 0}
                              className={`featured-filter-panel__chip featured-filter-panel__chip--all${picked.length === 0 ? " featured-filter-panel__chip--active" : ""}`}
                              onClick={() => setSizeFilter((prev) => prev.filter((s) => !s.startsWith(`${group.key}:`)))}
                            >
                              See all
                            </button>
                            {group.sizes.map((size) => {
                              const pick = `${group.key}:${size}`;
                              const on = sizeFilter.includes(pick);
                              return (
                                <button
                                  key={pick}
                                  type="button"
                                  aria-pressed={on}
                                  className={`featured-filter-panel__chip${on ? " featured-filter-panel__chip--active" : ""}`}
                                  onClick={() => setSizeFilter((prev) => (on ? prev.filter((s) => s !== pick) : [...prev, pick]))}
                                >
                                  {size}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
            {brandOptions.length > 0 && (
              <div className="featured-filter-panel__section">
                <div className="featured-filter-panel__heading" id={`featured-filter-brand-${key}`}>Brand</div>
                <div className="featured-filter-panel__options" role="group" aria-labelledby={`featured-filter-brand-${key}`}>
                  {brandOptions.map((brand) => {
                    const on = brandFilter.includes(brand);
                    return (
                      <button
                        key={brand}
                        type="button"
                        aria-pressed={on}
                        className={`featured-sort__option${on ? " featured-sort__option--active" : ""}`}
                        onClick={() => setBrandFilter((prev) => (on ? prev.filter((b) => b !== brand) : [...prev, brand]))}
                      >
                        <span>{brand}</span>
                        {on && <PickedMark />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="featured-filter-panel__section featured-filter-panel__section--price">
              <div className="featured-filter-panel__heading">
                <span>Price</span>
              </div>
              <div className="price-filter__inputs">
                <label className="price-filter__field">
                  <span className="price-filter__label">From</span>
                  <div className="price-filter__input-wrap">
                    <span className="price-filter__dollar">$</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      className="price-filter__input"
                      value={priceFrom}
                      onChange={(e) => setPriceFrom(e.target.value)}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </label>
                <label className="price-filter__field">
                  <span className="price-filter__label">To</span>
                  <div className="price-filter__input-wrap">
                    <span className="price-filter__dollar">$</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      className="price-filter__input"
                      value={priceTo}
                      onChange={(e) => setPriceTo(e.target.value)}
                      placeholder={String(Math.ceil(highestPrice))}
                      min="0"
                    />
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // The magnifier (desktop bar, phone row): the title becomes the field and
  // takes the caret.
  const openTitleSearch = () => {
    // The field mounts on the next render and is focused after it, outside
    // this tap, and iOS Safari raises the keyboard only for a focus inside
    // one: a stand-in field takes it now, and the keyboard moves across.
    if (isTouchDevice()) primeSoftKeyboard();
    setOpenFilter(null);
    setInlineSearchOpen(true);
    focusTitleSearch();
  };

  // The x beside the query: empty it and give the title back. On a search
  // page that also means leaving it for the catalog page it came from.
  const clearTitleSearch = () => {
    setSearchQuery("");
    setInlineSearchOpen(false);
    leaveEmptySearch();
  };

  // The search field in the title's place (searchMode). An inline grid: the
  // hidden sizer holds the query (or the placeholder) in the field's own
  // face, and the input fills the same cell, so the field is exactly as wide
  // as its text and the clear x sits right after it, where it sat after the
  // quoted title. Emptying the field does not leave the page; leaving does
  // (Escape, or pressing away from an empty field), so a query can be
  // cleared and retyped in place.
  // `height` (the desktop bar) holds the field to the wheel's own row height
  // (titleRowPx), so the bar, and the rule under it, stay exactly where they
  // are when the title turns into the field; a text field is otherwise as
  // tall as its font's natural line, 13px taller than the wheel at 48px.
  const renderTitleSearch = (ref, height) => (
    <span className="featured-title-search" ref={ref} style={height ? { height } : undefined}>
      <span className="featured-title-search__sizer" aria-hidden="true">{searchQuery || "Search"}</span>
      <input
        type="search"
        enterKeyHint="search"
        className="featured-title-search__input"
        // size=1: a text field reports about 20 characters of width by
        // default, which kept the grid (and the clear x) far past the query.
        size={1}
        placeholder="Search"
        aria-label="Search the catalog"
        autoComplete="off"
        spellCheck="false"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => setInlineSearchOpen(true)}
        onBlur={() => {
          if (!searchQuery.trim()) {
            setSearchQuery("");
            setInlineSearchOpen(false);
            leaveEmptySearch();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setSearchQuery("");
            setInlineSearchOpen(false);
            leaveEmptySearch();
            e.currentTarget.blur();
          } else if (e.key === "Enter") {
            // Search now rather than after the pause, and put the
            // keyboard away.
            e.preventDefault();
            const term = searchQuery.trim();
            if (term) {
              clearTimeout(commitTimer.current);
              setCommittedQuery(term);
              writeQRef.current?.(term);
            }
            e.currentTarget.blur();
          }
        }}
      />
    </span>
  );

  return (
    <div className="featured-page">
      <div className="featured-divider"></div>
      <div className="featured-wrap">
        {featuredLoading ? (
          <div className="featured-loading" />
        ) : (
          <>
            <div ref={sentinelRef} className="featured-filter-sentinel" />
            {/* Mobile-only: title + search that scroll away naturally */}
            <div
              className={`featured-title-row${stuck ? " featured-title-row--stuck" : ""}${searchMode ? " featured-title-row--searching" : ""}`}
              ref={mobileSearchRef}
            >
              {renderScrollUp(stuck && !movingDown)}
              {/* 32: the height the phone wheel's box comes to in this row (measured;
                  its itemHeight is 36), so the row and its rule do not move. */}
              {searchMode ? renderTitleSearch(undefined, 32) : (
              <SlotFilter
                ref={mobileTitleSlotRef}
                filters={displayQuery ? [{ key: "search", label: `"${displayQuery}"` }] : titlePages}
                activeIndex={displayQuery ? 0 : (currentPageIndex >= 0 ? currentPageIndex : 0)}
                onChangeIndex={committedQuery ? () => {} : handleTitleSlotChange}
                onClickCurrent={
                  committedQuery
                    ? (atTop
                        ? () => {
                            setSearchQuery("");
                            navigate("/featured");
                          }
                        : handleTitleClick)
                    : (atTop ? handleTitleNext : handleTitleClick)
                }
                enabled={!committedQuery && atTop}
                slot3d={true}
                horizontalOnly={true}
                itemHeight={36}
                className={`slot-filter--page-title${titleFade === "out" ? " slot-filter--title-fade-out" : " slot-filter--title-fade-in"}${peeking ? " slot-filter--peek-next" : ""}`}
                initialSlide={!committedQuery && initialLoad}
              />
              )}
              {searchQuery.trim() && (
                <button
                  type="button"
                  className="featured-search-clear"
                  aria-label="Clear search"
                  onClick={clearTitleSearch}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
              <div className="featured-filter-search">
                <button
                  type="button"
                  className="featured-filter-search__icon"
                  onClick={openTitleSearch}
                  aria-label="Search the catalog"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false" aria-hidden="true">
                    <path d="m21 21-4.34-4.34" />
                    <circle cx="11" cy="11" r="8" />
                  </svg>
                </button>
              </div>
            </div>
            <div className={`featured-desktop-title${sizeFilter.length > 0 && !searchMode ? " featured-desktop-title--size" : ""}${stuck ? " featured-desktop-title--stuck" : ""}${titleSettled ? " featured-desktop-title--settled" : ""}${searchMode ? " featured-desktop-title--searching" : ""}`} ref={filterBarRef}>
              {/* Shown only while the row is stuck AND settled: the arrival
                  guard keeps the row static for its first 600ms, and a static
                  row could not place an absolute child. And never while the
                  reader is still scrolling down (movingDown). */}
              {renderScrollUp(stuck && titleSettled && !movingDown)}
              <span
                className="featured-title-hover"
                ref={titleHoverRef}
              >
              {/* The picked sizes over the title, small and grey, as the
                  showcase sets SHOWCASE over a brand's name (the owner,
                  2026-10-02): "SIZE 1, 44". */}
              {sizeFilter.length > 0 && !searchMode && (
                <span className={`featured-size-crumb${crumbOut ? " featured-size-crumb--out" : ""}`}>{sizeCrumb}</span>
              )}
              {/* And SEARCH over a query, in the same small grey, so the
                  title reads as a search rather than a page (the owner,
                  2026-10-16). Not over the empty field, whose own
                  placeholder already says SEARCH. */}
              {searchQuery.trim() && (
                <span className="featured-size-crumb">Search</span>
              )}
              {searchMode ? renderTitleSearch(titleSearchRef, titleRowPx) : (
              <SlotFilter
                ref={titleSlotRef}
                filters={titleFilters}
                activeIndex={displayQuery ? 0 : (currentPageIndex >= 0 ? currentPageIndex : 0)}
                onChangeIndex={committedQuery ? () => {} : handleTitleSlotChange}
                onClickCurrent={
                  committedQuery
                    ? (atTop
                        ? () => {
                            setSearchQuery("");
                            navigate("/featured");
                          }
                        : handleTitleClick)
                    : (atTop ? handleTitleNext : handleTitleClick)
                }
                enabled={!committedQuery && atTop}
                stacked={true}
                slot3d={true}
                itemHeight={titleRowPx}
                className={`slot-filter--page-title${titleFade === "out" ? " slot-filter--title-fade-out" : " slot-filter--title-fade-in"}${peeking ? " slot-filter--peek-next" : ""}`}
                stuck={!committedQuery && stuck}
                initialSlide={!committedQuery && initialLoad}
              />
              )}
              </span>
              {/* Clear sits against the results title rather than across the
                  bar in the filter group: the thing it undoes is the title, so
                  it belongs next to it and is a short trip from reading the
                  term to dismissing it. Same control the mobile title row has
                  had. Outside .featured-title-hover so pointing at it does not
                  trigger the title's own hover behaviour. */}
              {searchQuery.trim() && (
                <button
                  type="button"
                  className="featured-search-clear featured-search-clear--desktop"
                  aria-label="Clear search"
                  onClick={clearTitleSearch}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
              <div className="featured-filter-bar__right">
                {/* The magnifier hands the caret to the title, which is the
                    search field now (renderTitleSearch); the small field that
                    used to slide out here beside FILTER is gone. */}
                <div className="featured-filter-search" ref={inlineSearchRef}>
                  <button
                    type="button"
                    className="featured-filter-search__icon"
                    onClick={openTitleSearch}
                    aria-label="Search the catalog"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false" aria-hidden="true">
                      <path d="m21 21-4.34-4.34" />
                      <circle cx="11" cy="11" r="8" />
                    </svg>
                  </button>
                </div>

                {renderFilterControl(filterRef, "filter", false)}

              </div>
            </div>
            <div className="featured-count-bar">
              {/* Always products, as the showcase count words it (1 PRODUCT,
                  7 PRODUCTS). A category filter used to swap the word for the
                  category name (7 TOPS). */}
              <span className="featured-filter-bar__count">{productCount} {productCount === 1 ? "Product" : "Products"}</span>
              <div className="featured-count-bar__mobile-filters">
                {renderFilterControl(mobileFilterRef, "filter-mobile", true)}
              </div>
            </div>
            <div className="featured-grid" key={viewMode} ref={gridRef}>
              {/* displayItems trails filteredItems through a filter change
                  (the grid motion, above). */}
              {displayItems.map((item, index) => (
                // `state.from` lets the product page's back arrow return to
                // this exact tab / search (see ProductPage `backTo`).
                <ProductCard
                  key={`${viewMode}-${item.id}`}
                  item={item}
                  index={index}
                  staggerMs={revealStagger.current ? 120 : 0}
                  className={exitingIds.has(item.id) ? "product-card--exiting" : ""}
                  to={viewMode === "featured" ? `/featured/item/${item.id}` : `/featured/${viewMode}/item/${item.id}`}
                  state={{ from: `${location.pathname}${location.search}` }}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {/* Down caret, fixed at the bottom centre of the viewport: shown while
          the reader is at the top and there is more below the fold; one press
          brings the next rows up. Its twin, the up caret in the stuck title
          band (renderScrollUp), is the way back. The old bottom-left
          back-to-top is gone: that job moved to the top of the page. */}
      <button
        type="button"
        /* Shown for as long as there is anything below, not only at the top:
           it is the way to keep going, so it has to stay reachable all the
           way down. It hides once the footer shows, where it would point at
           nothing and sit over the footer's links (utils/scrollCue). */
        className={`featured-scroll-down${hasBelow ? " featured-scroll-down--shown" : ""}`}
        aria-label="Scroll down"
        tabIndex={hasBelow ? 0 : -1}
        aria-hidden={!hasBelow}
        onClick={handleScrollDown}
      >
        {/* Thin, a touch heavier than the arrows beside the title (SlotFilter: strokeWidth
            2 in a 14x8 box); this box is 24x14, so 1.17 viewBox units render
            at 2px, the site-wide floor for arrows. While shown, the glyph drifts down and fades on a
            loop (CSS), a cue that the page goes on. The up caret matches. */}
        <svg className="featured-scroll-caret__glyph" width="24" height="14" viewBox="0 0 14 8" fill="none" stroke="currentColor"
          strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M1 1L7 7L13 1" />
        </svg>
      </button>
      <Footer />
    </div>
  );
}
